"use client"

import { PromotionProductCard } from "@/components/organisms/ProductCard/PromotionProductCard"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { HttpTypes } from "@medusajs/types"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface PromotionSectionProps {
  products?: (HttpTypes.StoreProduct & { seller?: any })[]
  locale?: string
  heading?: string
  subtitle?: string
  ctaText?: string
  home?: boolean
}

const VISIBLE_PRODUCTS_INITIAL = 4
const MAX_PRODUCTS_TO_DISPLAY = 8
const OBSERVER_ROOT_MARGIN = "100px"
const OBSERVER_THRESHOLD = 0.01

export const PromotionSection = ({
  products = [],
  locale = process.env.NEXT_PUBLIC_DEFAULT_REGION || "pl",
  heading,
  subtitle,
  ctaText,
  home
}: PromotionSectionProps) => {
  const [visibleProducts, setVisibleProducts] = useState<Set<number>>(new Set())
  const productRefs = useRef<(HTMLDivElement | null)[]>([])
  const observerRef = useRef<Map<number, IntersectionObserver>>(new Map())

  // Initialize visible products with first 4 items
  useEffect(() => {
    const initialVisible = new Set<number>()
    for (let i = 0; i < Math.min(VISIBLE_PRODUCTS_INITIAL, products.length); i++) {
      initialVisible.add(i)
    }
    setVisibleProducts(initialVisible)
  }, [products.length])

  // Intersection Observer callback
  const handleIntersection = useCallback((index: number) =>
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleProducts((prev) => {
            const newSet = new Set(prev)
            newSet.add(index)
            return newSet
          })
          // Disconnect observer once product is visible
          const observer = observerRef.current.get(index)
          if (observer) {
            observer.disconnect()
            observerRef.current.delete(index)
          }
        }
      })
    },
    [])

  // Setup Intersection Observers
  useEffect(() => {
    const observers = observerRef.current

    productRefs.current.forEach((ref, index) => {
      // Skip if already visible or no ref
      if (visibleProducts.has(index) || !ref || index < VISIBLE_PRODUCTS_INITIAL) {
        return
      }

      // Clean up existing observer for this index
      const existingObserver = observers.get(index)
      if (existingObserver) {
        existingObserver.disconnect()
        observers.delete(index)
      }

      // Create new observer
      const observer = new IntersectionObserver(handleIntersection(index), {
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: OBSERVER_THRESHOLD,
      })

      observer.observe(ref)
      observers.set(index, observer)
    })

    return () => {
      // Cleanup all observers
      observers.forEach((observer) => observer.disconnect())
      observers.clear()
    }
  }, [products.length, handleIntersection, visibleProducts])

  // Memoized products to display
  const productsToDisplay = useMemo(() =>
    products.slice(0, MAX_PRODUCTS_TO_DISPLAY),
    [products]
  )

  // Handle empty products
  if (products.length === 0) {
    return null
  }

  return (
    <section
      className="relative w-full"
      aria-label="New Arrivals Section"
    >
      <div className="mx-auto w-full">
        {/* Section Header */}
        <div className="mb-12">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {heading}
          </h2>
          <p className="mt-2 text-base text-muted-foreground lg:text-lg">
            {subtitle}
          </p>
        </div>

        {/* Products Grid */}
        <div className="flex justify-center">
          <div
            className="inline-grid grid-cols-4 2xl:grid-cols-5 gap-[30px] w-full"
            role="list"
            aria-label="New arrival products"
          >
            {productsToDisplay.map((product, index) => (
              <div
                key={product.id}
                ref={(el) => {
                  productRefs.current[index] = el
                }}
                className="group relative"
                role="listitem"
              >
                {visibleProducts.has(index) ? (
                  <div
                    className="relative rounded-lg bg-secondary/5 transition-all duration-300 hover:bg-secondary/10 flex justify-center"
                    aria-label={`Product: ${product.title}`}
                  >
                    <PromotionProductCard
                      product={product}
                      api_product={product}
                      locale={locale}
                      showAddToCartButton={false}
                      showVariantCount={true}
                      className="!max-w-none !min-w-0"
                    />
                  </div>
                ) : (
                  <div
                    className="aspect-[3/4] animate-pulse rounded-lg bg-gray-100"
                    aria-label="Loading product"
                    role="status"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* View All Link */}
        <div className="mt-12 text-center">
          <LocalizedClientLink
            href="/categories"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 transition-all hover:underline group"
            aria-label="View all featured products"
          >
            {ctaText}
            <svg
              className="size-4 transition-transform group-hover:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </LocalizedClientLink>
        </div>
      </div>
    </section>
  )
}